import {
  APPROVAL_DECISION,
  CHUNK_TYPE,
  EMPTY_CHAT,
  ERROR_MESSAGE,
  RESUME_COMMAND_TYPE,
  Role,
} from '~/constants/conversationModels';

/**
 * Maps a `turn_event.event_type` string onto the CHUNK_TYPE constant that already drives chunk
 * building/rendering for the live SSE path. Unmapped types (e.g. `query`, `interrupt_response`,
 * `goto`, handled separately per-turn below) pass through unchanged and fall to
 * applyEventToChunks' no-op default.
 */
const EVENT_TYPE_TO_CHUNK_TYPE = {
  messages: CHUNK_TYPE.TEXT,
  tools: CHUNK_TYPE.TOOL_CALLS,
  references: CHUNK_TYPE.REFERENCES,
  interrupts: CHUNK_TYPE.INTERRUPT,
  'custom:image_gen': CHUNK_TYPE.IMAGE_GEN,
  execution_error: CHUNK_TYPE.ERROR,
  translation: CHUNK_TYPE.TRANSLATION,
};

/**
 * UNCONFIRMED with backend — best-effort inference, not a spec'd contract. Backend docs describe
 * an execution_error's `source` field (values seen: 'graph', 'persistence', and unnamed "post-run
 * source" values) and separately say to "branch on blocking, not on the event being present" —
 * blocking means the turn failed, non-blocking means the answer is fine and only something
 * peripheral (citations/title) was lost. Confirmed via the OpenAPI schema example: the payload
 * has an explicit `blocking` boolean (e.g. `{ source: 'graph', blocking: true }`) — this is no
 * longer an inference from wording, unlike the earlier `source === 'graph'` guess it replaced.
 */
const isBlockingExecutionError = (payload) => Boolean(payload?.blocking);

export const mapTurnRole = (role) => (role === 'user' ? Role.user : Role.chatbot);

/**
 * Where collection/scope metadata lives inside a turn's `requestMetadata` isn't settled with
 * backend yet. Stubbed so the scoped-conversation banner logic (SetConversationScope) has a
 * single call site to update once the shape is confirmed.
 */
export const extractCollectionId = () => undefined;

const isTextDelta = (payload) => payload?.event === 'content-block-delta' && payload?.delta?.type === 'text-delta';

/**
 * A completed message's `content` isn't always a string — Bedrock returns a block list.
 * Joins the text out of whichever shape shows up; non-text blocks (e.g. inline tool_use) are
 * skipped rather than stringified.
 */
const normalizeMessageContent = (content) => {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return content ?? '';

  return content
    .map((block) => {
      if (typeof block === 'string') return block;
      if (typeof block?.text === 'string') return block.text;
      return '';
    })
    .join('');
};

/**
 * Folds one event's payload into a message's `chunks` array, mutating and returning the same
 * array. Shared between the live-SSE `AddChunkSSE` mutation (conversationStore.js) and the
 * history-replay builder below (buildMessagesFromTurns), so both produce identical chunk
 * shapes for the same event vocabulary instead of two parsers drifting apart.
 *
 * @param {object[]} chunks - existing chunks array (already a caller-owned copy)
 * @param {string} chunkType - a CHUNK_TYPE constant
 * @param {object} payload
 */
export const applyEventToChunks = (chunks, chunkType, payload) => {
  switch (chunkType) {
    case CHUNK_TYPE.TOOL_CALLS: {
      chunks.push({ type: CHUNK_TYPE.TOOL_CALLS, data: payload });
      break;
    }
    case CHUNK_TYPE.INTERRUPT:
      chunks.push({ type: CHUNK_TYPE.INTERRUPT, data: payload });
      break;
    case CHUNK_TYPE.IMAGE_GEN: {
      const chunkIdx = chunks.findIndex((c) => c.type === CHUNK_TYPE.IMAGE_GEN);
      const existing = chunkIdx >= 0 ? chunks[chunkIdx] : null;
      const images = existing ? [...existing.images] : [];

      if (payload.event === 'image-started') {
        images.push({ id: payload.imageId, status: 'loading', url: null, params: payload.params });
      } else {
        const imgIdx = images.findIndex((img) => img.id === payload.imageId);
        if (imgIdx >= 0) {
          images[imgIdx] = {
            ...images[imgIdx],
            status: payload.event === 'image-finished' ? 'finished' : 'error',
          };
        }
      }

      const updated = { type: CHUNK_TYPE.IMAGE_GEN, images };
      if (chunkIdx >= 0) {
        chunks[chunkIdx] = updated;
      } else {
        chunks.push(updated);
      }
      break;
    }
    case CHUNK_TYPE.REFERENCES:
      chunks.push({ type: CHUNK_TYPE.REFERENCES, data: payload });
      break;
    case CHUNK_TYPE.TRANSLATION:
      // Never streamed live (translation still arrives over Ably as a legacy `message.data`
      // block) — this only fires during history replay. Same payload shape as the legacy
      // block minus conversationId, so TargetSourceTranslationBlock renders it unchanged.
      chunks.push({ type: CHUNK_TYPE.TRANSLATION, data: payload });
      break;
    case CHUNK_TYPE.TEXT: {
      if (isTextDelta(payload)) {
        const text = payload.delta.text;
        const lastChunk = chunks[chunks.length - 1];
        if (lastChunk?.type === CHUNK_TYPE.TEXT) {
          lastChunk.content += text;
        } else {
          chunks.push({ type: CHUNK_TYPE.TEXT, content: text });
        }
      } else {
        // History replay: only message-finish payloads are persisted (no deltas), so the
        // content is already complete — set it in one shot rather than appending.
        chunks.push({ type: CHUNK_TYPE.TEXT, content: normalizeMessageContent(payload?.content) });
      }
      break;
    }
    case CHUNK_TYPE.ERROR: {
      // Blocking errors mean the turn itself failed — always shown, falling back to a generic
      // message if the backend didn't send one. A non-blocking error usually means the answer is
      // fine and only something peripheral (citations/title) was lost, so it stays silent by
      // default — but a non-blocking error can still carry a genuinely user-facing message (e.g.
      // "Something went wrong in downloading the translated file"), so render it whenever one is
      // present rather than dropping it just because the turn didn't fail outright.
      const blocking = isBlockingExecutionError(payload);
      if (blocking || payload?.message) {
        chunks.push({ type: CHUNK_TYPE.ERROR, content: payload?.message || ERROR_MESSAGE });
      }
      break;
    }
    case 'lifecycle':
    case 'conversation':
      // No visual chunk for either: lifecycle has nothing left to signal once a turn is
      // complete, and conversation (title) is already known from the parent conversation object.
      break;
    default:
      console.log('[applyEventToChunks] Unhandled event/chunk type', chunkType);
  }
  return chunks;
};

/**
 * Builds a legacy-shaped `messages` array from the new agent's `turns`/`events` history
 * (see turn-schema-migration-plan.md). One turn becomes one message: user turns read their
 * `query` event's text directly; assistant turns fold their events through the same
 * applyEventToChunks used by live streaming.
 *
 * User turns with no `query` event and no typed text on a `goto` event either — `interrupt_response`
 * (e.g. an image-style pick) or a bare remix click with nothing typed — never render their own
 * bubble, same as the old agent: answering an inline HITL prompt or requesting a plain remix isn't
 * itself shown as a chat message, only the continuation that follows is. A `goto` (remix) turn
 * WITH typed text (`payload.query` non-null) does render a bubble, matching the live behavior of
 * publishToConversation's AddMessage dispatch, which shows a bubble for any non-empty typed text
 * regardless of whether the send is a plain query or a remix.
 *
 * A `query`/`goto` payload with `type === 'promptCard'` carries no rendered template text (the
 * backend re-renders that server-side from versionContentId + variables), but the title now
 * does travel with it — buildAssistQuery sends it alongside versionContentId/variables
 * specifically so it comes back on reload without needing a fetch (see PromptCardQuery in
 * services/api/conversations.js).
 *
 * @param {object[]} turns
 */
export const buildMessagesFromTurns = (turns = []) => {
  const sortedTurns = [...turns].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  return sortedTurns
    .map((turn, index) => {
      const role = mapTurnRole(turn.role);
      const collectionId = extractCollectionId(turn.requestMetadata);

      const message = {
        ...EMPTY_CHAT,
        messageId: turn.id,
        role,
        timestamp: turn.createdAt,
        isUser: role === Role.user,
        isTyping: false,
        chunks: [],
        ...(collectionId ? { collectionId } : {}),
      };

      if (role === Role.user) {
        const queryEvent = (turn.events || []).find((event) => event.eventType === 'query');
        // A `goto` (remix) turn only skips the bubble when the user didn't type anything
        // alongside it — live, publishToConversation's AddMessage dispatch works the same way
        // (it only adds a user bubble when `data`/textMessage is non-empty), so a remix with
        // typed text shows a bubble live and must show one on reload too.
        const gotoEvent = (turn.events || []).find((event) => event.eventType === 'goto');
        const queryPayload = queryEvent?.payload || gotoEvent?.payload?.query || null;

        if (!queryPayload) {
          return null;
        }

        if (queryPayload.type === 'promptCard') {
          message.versionContentId = queryPayload.versionContentId;
          message.promptCardVariables = queryPayload.variables || [];
          message.promptCardTitle = queryPayload.title || '';
          message.filters = turn.requestMetadata?.filters || [];
          return message;
        }

        message.message = queryPayload.text ?? '';
        return message;
      }

      let chunks = [];
      (turn.events || []).forEach((event) => {
        const chunkType = EVENT_TYPE_TO_CHUNK_TYPE[event.eventType] ?? event.eventType;
        chunks = applyEventToChunks(chunks, chunkType, event.payload);
      });

      // A turn's events are complete history, unlike live streaming — an image left 'loading'
      // here means its image-finished/error event never arrived (e.g. the user closed the tab
      // mid-generation), not that it's still in progress. Without this the spinner never resolves.
      chunks = chunks.map((chunk) => {
        if (chunk.type !== CHUNK_TYPE.IMAGE_GEN) return chunk;
        return {
          ...chunk,
          images: chunk.images.map((image) => (image.status === 'loading' ? { ...image, status: 'error' } : image)),
        };
      });

      // An interrupted turn that ISN'T the last turn in the conversation must have been
      // resolved — the graph can't have continued past the pause otherwise. There's no explicit
      // id linking a response back to its interrupt (see turn-schema-migration-plan.md), so the
      // decision is read positionally off the very next turn's interrupt_response. Only the last
      // turn, if interrupted, is genuinely still pending — leave that one's chunk untouched so it
      // renders the live approve/reject form.
      const isLastTurn = index === sortedTurns.length - 1;
      if (!isLastTurn) {
        const nextTurn = sortedTurns[index + 1];
        const resolution = (nextTurn?.events || []).find((event) => event.eventType === 'interrupt_response');
        if (resolution) {
          // hitlResponse carries one decision per action_request (positional); imageStyleChoicesResponse
          // carries no decisions array at all (there's only ever the one image-gen choice to make) —
          // 'submitted' matches what ImageStyleChoiceInterrupt already emits for a live submission.
          const decisions =
            resolution.payload?.type === RESUME_COMMAND_TYPE.HITL_RESPONSE ? resolution.payload?.decisions || [] : [];
          chunks = chunks.map((chunk) => {
            if (chunk.type !== CHUNK_TYPE.INTERRUPT) return chunk;
            return {
              ...chunk,
              data: chunk.data.map((interrupt, i) => ({
                ...interrupt,
                resolvedDecision: decisions[i]?.type || decisions[0]?.type || APPROVAL_DECISION.SUBMITTED,
              })),
            };
          });
        }
      }

      message.chunks = chunks;
      // Keep the legacy `message` text field in sync for any code still reading it directly.
      message.message = chunks
        .filter((chunk) => chunk.type === CHUNK_TYPE.TEXT)
        .map((chunk) => chunk.content)
        .join('');

      return message;
    })
    .filter(Boolean);
};
